import { AutocompletAble } from 'ui/DropdownItems/types';
import React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material';
import { useParams } from 'react-router-dom';
import { translateBlock } from 'i18n';
import Loading from 'components/Loading';
import { trackEventBudget, TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import { useGetIntegrations } from 'helpers/integrations';
import {
    BudgetProposalT,
    BudgetVentilatedDataWithCadrageT,
} from 'routes/BudgetModule/config/types';
import TabulatedPage, {
    TabulatedPageTab,
} from 'ui/TabulatedPage/TabulatedPage';
import NotFound from 'components/Errors/NotFound';
import BudgetChartSection from 'routes/BudgetModule/components/shared/BudgetChart/BudgetChartSection/BudgetChartSection';
import BudgetHeader from 'routes/BudgetModule/components/shared/BudgetHeader/BudgetHeader';
import BudgetContent from 'routes/BudgetModule/components/shared/BudgetContent';
import { useGetSearchParam } from 'helpers/urlHelpers';
import { BudgetProposalVentilationBy } from 'routes/BudgetModule/config/queries';
import {
    BUDGET_PROPOSAL_VENTILATION_BY_OPERATION,
    BUDGET_PROPOSAL_VENTILATION_BY_RESOURCE,
    BUDGET_PROPOSAL_VIEW_BY,
} from 'routes/BudgetModule/config/constants';
import {
    isCustomBudgetProposal,
    useGetBudgetProposal,
    useGetCustomFilters,
} from './Resource/hooks/budgetProposal';
import BudgetVentilatedByOperation from './BudgetVentilated/BudgetVentilatedByOperation';
import './_budgetProposal.scss';
import 'routes/BudgetModule/components/BudgetProposal/shared/Grids/_commonGrid.scss';
import { useGetBudgetVentilated } from './helpers/hooks/getBudgetVentilated';
import HttpErrorWrapper from './Resource/ExpenseIncome/components/HttpErrorWrapper';
import { BudgetProposalByResource } from './Resource/BudgetProposalByResource';
import BudgetReportToggle from '../shared/BudgetReportToggle/BudgetReportToggle';
import Flex from 'ui/Flex/Flex';
import { useGetBudgetProposalsInscriptions } from 'routes/BudgetModule/helpers/hooks/useGetInscriptions';
import { FALLBACK_SECONDARY_BUDGET_AXIS_DISPLAY } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableProvider';
import { pluralize } from 'helpers/formatHelper';
import { capitalize } from 'helpers/helpers';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

const StyTabulatedPage = styled(TabulatedPage, {
    name: 'StyTabulatedPage',
})({
    alignSelf: 'stretch',
    paddingBottom: '3rem',
});

const StyBudgetHeader = styled(BudgetHeader, {
    name: 'StyBudgetProposalHeader',
})({
    boxShadow: 'none',
});

function BudgetProposal({
    budgetProposal,
}: {
    budgetProposal: BudgetProposalT;
}): React.ReactElement {
    const { t } = useTranslation();
    const { budgetProposalId } = useParams<{ budgetProposalId: string }>();
    const budgetProposalPath = `/budget_proposal/${budgetProposalId}`;

    const { data: integrations, isLoading: isIntegrationLoading } =
        useGetIntegrations();

    const { code, setCodes, codeFilters, codeOptions } =
        useGetCustomFilters(integrations);
    const isSchool = isCustomBudgetProposal(integrations);

    // We are fetching all inscriptions for the budget proposal, only for the Excel export
    // That doesn't seem very optimized.
    const {
        data: inscriptionsData,
        isLoading: areInscriptionsLoading,
        isError: isFetchInscriptionsError,
        error: inscriptionError,
    } = useGetBudgetProposalsInscriptions({
        budgetBy: budgetProposal.by,
        budgetProposalIds: [budgetProposal.id],
        additionalFilters: codeFilters,
        phase: budgetProposal.phase,
    });

    if (isFetchInscriptionsError) {
        console.error("Couldn't not fetch inscription data");
    }

    const paramsViewBy = useGetSearchParam(
        BUDGET_PROPOSAL_VIEW_BY,
        BUDGET_PROPOSAL_VENTILATION_BY_RESOURCE,
    );
    const viewBy: BudgetProposalVentilationBy = [
        BUDGET_PROPOSAL_VENTILATION_BY_RESOURCE,
        BUDGET_PROPOSAL_VENTILATION_BY_OPERATION,
    ].includes(paramsViewBy)
        ? (paramsViewBy as BudgetProposalVentilationBy)
        : BUDGET_PROPOSAL_VENTILATION_BY_RESOURCE;

    const {
        data: budgetVentilatedData,
        isLoading: isBudgetVentilatedLoading,
        query: budgetVentilatedQuery,
        isError: isBudgetVentilatedError,
        error: budgetVentilatedError,
    } = useGetBudgetVentilated(
        budgetProposal,
        viewBy,
        code?.value,
        codeFilters,
    );
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    if (
        areInscriptionsLoading ||
        isIntegrationLoading ||
        isBudgetVentilatedLoading ||
        (!budgetVentilatedData && !isBudgetVentilatedError) ||
        (!inscriptionsData && !isFetchInscriptionsError)
    ) {
        return (
            <div className="budget-loader">
                <Loading pastDelay />
            </div>
        );
    }

    const setCodesAndTrack = (codes: AutocompletAble<string> | null) => {
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetProposal.filterBudgetLineCode,
            {
                code: codes?.label,
            },
        );
        setCodes(codes);
    };

    const budgetProposalByResource = (
        <BudgetProposalByResource
            budgetProposal={budgetProposal}
            code={code}
            setCodes={setCodesAndTrack}
            codeOptions={codeOptions}
            isError={isFetchInscriptionsError || isBudgetVentilatedError}
            error={inscriptionError || budgetVentilatedError}
            inscriptionsData={inscriptionsData}
            budgetVentilatedData={
                budgetVentilatedData as
                    | BudgetVentilatedDataWithCadrageT[]
                    | undefined
            }
            budgetVentilatedQuery={budgetVentilatedQuery}
            integrations={integrations}
        />
    );

    // if code is not null
    // there is custom filters so we should return custom view with only BudgetProposal by resource
    if (code !== null) {
        return (
            <div className="page-container budget-proposal-wrapper dx-viewport">
                <StyBudgetHeader
                    headerTitle={t('budgetProposal.headerTitle')}
                    parentLink={budgetProposalPath}
                    budgetProposal={budgetProposal}
                    exercice={budgetProposal.exercice}
                />
                {budgetProposalByResource}
            </div>
        );
    }
    const sourceTableLabel = sourceTable
        ? capitalize(pluralize(translateBlock(sourceTable)))
        : capitalize(pluralize(FALLBACK_SECONDARY_BUDGET_AXIS_DISPLAY));
    const tabsList: TabulatedPageTab[] = [
        {
            label: t('budgetProposal.viewByResource', {
                resourceType: translateBlock(budgetProposal.by, true),
            }),
            pathname: `/budget_proposal/${budgetProposalId}`,
            search: '?viewBy=resource',
            children: budgetProposalByResource,
            disabled: false,
            onClick: () => {
                trackEventBudget(
                    TRACKABLE_EVENTS.budget.budgetProposal
                        .viewByResourceTabClicked,
                    {
                        exercice: budgetProposal.exercice,
                        budgetProposal: budgetProposal.name,
                        section: budgetProposal.section,
                    },
                );
            },
        },
        {
            label: t('budgetProposal.viewBySecondaryBudgetAxisElement', {
                sourceTable: sourceTableLabel,
            }),
            pathname: `/budget_proposal/${budgetProposalId}`,
            search: '?viewBy=operation',
            children: (
                <BudgetContent sx={!isSchool ? { pt: '1rem' } : undefined}>
                    <HttpErrorWrapper
                        error={inscriptionError || budgetVentilatedError}
                        isError={
                            isFetchInscriptionsError || isBudgetVentilatedError
                        }
                    >
                        {!isSchool && (
                            <Flex direction="row" justifyContent="end">
                                <BudgetReportToggle
                                    exercice={budgetProposal.exercice}
                                    context={{
                                        page: 'Dépenses/Recettes',
                                        budgetProposal: budgetProposal.name,
                                        section: budgetProposal.section ?? '',
                                    }}
                                />
                            </Flex>
                        )}
                        <BudgetChartSection
                            budgetProposal={budgetProposal}
                            ventilationType="operation"
                            ventilatedData={{
                                query: budgetVentilatedQuery,
                                data: budgetVentilatedData!,
                            }}
                        />
                        <BudgetVentilatedByOperation
                            budgetProposal={budgetProposal}
                            inscriptionsData={inscriptionsData!}
                            ventilatedData={budgetVentilatedData!}
                        />
                    </HttpErrorWrapper>
                </BudgetContent>
            ),
            disabled: false,
            onClick: () => {
                trackEventBudget(
                    TRACKABLE_EVENTS.budget.budgetProposal
                        .viewByOperationTabClicked,
                    {
                        exercice: budgetProposal.exercice,
                        budgetProposal: budgetProposal.name,
                        section: budgetProposal.section,
                    },
                );
            },
        },
    ];

    return (
        <div className="page-container budget-proposal-wrapper dx-viewport">
            <StyBudgetHeader
                headerTitle={t('budgetProposal.headerTitle')}
                parentLink={budgetProposalPath}
                budgetProposal={budgetProposal}
                exercice={budgetProposal.exercice}
            />
            <StyTabulatedPage
                tabsList={tabsList}
                variant="fullWidth"
                tabsStyles={{
                    zIndex: 3,
                    position: 'sticky',
                    top: '78px',
                    boxShadow: '4px 8px 24px rgba(0, 0, 0, 0.06)',
                }}
            />
        </div>
    );
}

const BudgetProposalWrapper = (): React.ReactElement => {
    const { budgetProposalId } = useParams();
    const { t } = useTranslation();

    const {
        isLoading: isBudgetProposalLoading,
        error,
        data: budgetProposal,
    } = useGetBudgetProposal({
        budgetProposalId: budgetProposalId
            ? parseInt(budgetProposalId, 10)
            : -1,
    });

    const isLoading = isBudgetProposalLoading;
    const isBudgetProposalEmpty = _.isEmpty(budgetProposal);

    React.useEffect(() => {
        if (isLoading || isBudgetProposalEmpty) return;

        trackEventBudget(TRACKABLE_EVENTS.budget.budgetProposal.pageOpened, {
            budgetProposal: budgetProposal!.name,
            phase: t(`budgetProposal.${budgetProposal!.phase}`),
        });
    }, [isLoading || isBudgetProposalEmpty]);

    if (error && error.status === 404) {
        return <NotFound />;
    }

    if (isLoading || isBudgetProposalEmpty || !budgetProposal) {
        return (
            <div className="budget-loader">
                <Loading pastDelay />
            </div>
        );
    }

    return <BudgetProposal budgetProposal={budgetProposal} />;
};

export default BudgetProposalWrapper;
